import React, { useRef, useState, useEffect } from 'react';
import { Button } from 'src/ui/Button';
import { Input } from 'src/ui/Input';
import {
  AccountStatus,
  AccountStatusLabel,
  FetchManyUsersDto,
  INTERVIEW_TOPIC,
  INTERVIEW_TOPIC_LABEL,
  MembershipType,
  MembershipTypeLabel,
  OnboardingStep,
  ONBOARDING_STEP_LABEL,
  ProfileGender,
  ProfileGenderLabel,
  UserType,
  UserTypeLabel,
} from 'src/store/userApi';

export interface UserFilterState {
  gender?: number[];
  onboardingStep?: number[];
  // INTERVIEW_TOPIC values plus 'none' to mean "no current topic".
  interviewTopic?: (INTERVIEW_TOPIC | 'none')[];
  accountStatus?: number[];
  membershipType?: number[];
  userType?: number[];
  ageMin?: number;
  ageMax?: number;
}

interface UserFiltersProps {
  value: UserFilterState;
  onChange: (next: UserFilterState) => void;
  /**
   * Values that are always selected and cannot be toggled off (rendered as
   * disabled, on chips). Used by win-back to pin the ACTIVE / NORMAL safety
   * baseline. Backward compatible — omit for the normal user-list behaviour.
   */
  locked?: UserFilterState;
  /**
   * Renders the filter sections directly in normal document flow instead of
   * behind a "Filters" button + floating popover. The popover is positioned
   * absolute relative to the button and sized for a page toolbar with open
   * space below it — inside a modal's own scrollable, height-constrained
   * body it gets clipped instead of scrolling. Used by InviteByFilterModal.
   */
  inline?: boolean;
}

const GENDER_VALUES: ProfileGender[] = [
  ProfileGender.FEMALE,
  ProfileGender.MALE,
  ProfileGender.NON_BINARY,
];

const ONBOARDING_STEP_VALUES: OnboardingStep[] = [
  OnboardingStep.START,
  OnboardingStep.READY_FOR_INTERVIEW,
  OnboardingStep.INTERVIEW,
  OnboardingStep.UPLOAD_PHOTO,
  OnboardingStep.PLAN_COMPARISON,
  OnboardingStep.SUBSCRIPTION,
  OnboardingStep.REVIEW_PROFILE,
  OnboardingStep.COMPLETED,
];

const ACCOUNT_STATUS_VALUES: AccountStatus[] = [
  AccountStatus.ACTIVE,
  AccountStatus.BLOCKED,
  AccountStatus.PAUSED,
  AccountStatus.SKIP_MATCH,
];

const MEMBERSHIP_TYPE_VALUES: MembershipType[] = [
  MembershipType.FREEMIUM,
  MembershipType.PREMIUM_TRIAL,
  MembershipType.PREMIUM,
];

const USER_TYPE_VALUES: UserType[] = [
  UserType.NORMAL,
  UserType.SAMPLE,
  UserType.TEST,
];

const INTERVIEW_TOPIC_VALUES: (INTERVIEW_TOPIC | 'none')[] = [
  'none',
  INTERVIEW_TOPIC.INIT,
  INTERVIEW_TOPIC.LIFE_STYLE,
  INTERVIEW_TOPIC.FAMILY,
  INTERVIEW_TOPIC.RELATIONSHIP,
];

const INTERVIEW_TOPIC_DISPLAY: Record<INTERVIEW_TOPIC | 'none', string> = {
  none: 'Not started',
  ...INTERVIEW_TOPIC_LABEL,
};

// A dimension counts as "active" only if it has a value beyond the locked
// baseline (so win-back's pinned ACTIVE / NORMAL don't inflate the badge).
const hasBeyondLocked = <V,>(sel: V[] | undefined, lock: V[] | undefined) => {
  if (!sel || sel.length === 0) return false;
  const lockSet = new Set(lock ?? []);
  return sel.some((v) => !lockSet.has(v));
};

const countActive = (
  value: UserFilterState,
  locked?: UserFilterState
): number => {
  let n = 0;
  if (hasBeyondLocked(value.gender, locked?.gender)) n += 1;
  if (hasBeyondLocked(value.onboardingStep, locked?.onboardingStep)) n += 1;
  if (hasBeyondLocked(value.interviewTopic, locked?.interviewTopic)) n += 1;
  if (hasBeyondLocked(value.accountStatus, locked?.accountStatus)) n += 1;
  if (hasBeyondLocked(value.membershipType, locked?.membershipType)) n += 1;
  if (hasBeyondLocked(value.userType, locked?.userType)) n += 1;
  if (value.ageMin !== undefined || value.ageMax !== undefined) n += 1;
  return n;
};

export const userFiltersToQueryParams = (
  filters: UserFilterState
): Partial<FetchManyUsersDto> => {
  const params: Partial<FetchManyUsersDto> = {};
  if (filters.gender && filters.gender.length > 0) {
    params.gender = filters.gender.join(',');
  }
  if (filters.onboardingStep && filters.onboardingStep.length > 0) {
    params.onboardingStep = filters.onboardingStep.join(',');
  }
  if (filters.accountStatus && filters.accountStatus.length > 0) {
    params.accountStatus = filters.accountStatus.join(',');
  }
  if (filters.membershipType && filters.membershipType.length > 0) {
    params.membershipType = filters.membershipType.join(',');
  }
  if (filters.userType && filters.userType.length > 0) {
    params.userType = filters.userType.join(',');
  }
  if (filters.interviewTopic && filters.interviewTopic.length > 0) {
    params.interviewTopic = filters.interviewTopic.join(',');
  }
  if (filters.ageMin !== undefined) {
    params.ageMin = String(filters.ageMin);
  }
  if (filters.ageMax !== undefined) {
    params.ageMax = String(filters.ageMax);
  }
  return params;
};

const toggleNumber = (list: number[] | undefined, value: number): number[] => {
  const set = new Set(list ?? []);
  if (set.has(value)) set.delete(value);
  else set.add(value);
  return Array.from(set);
};

const toggleTopic = (
  list: (INTERVIEW_TOPIC | 'none')[] | undefined,
  value: INTERVIEW_TOPIC | 'none'
): (INTERVIEW_TOPIC | 'none')[] => {
  const set = new Set(list ?? []);
  if (set.has(value)) set.delete(value);
  else set.add(value);
  return Array.from(set);
};

interface SectionProps<V> {
  title: string;
  options: V[];
  selected: V[] | undefined;
  /** Always-on, non-removable values (rendered disabled + primary). */
  locked?: V[];
  labelOf: (v: V) => string;
  onToggle: (v: V) => void;
}

const FilterSection = <V extends string | number>({
  title,
  options,
  selected,
  locked,
  labelOf,
  onToggle,
}: SectionProps<V>) => {
  const selectedSet = new Set(selected ?? []);
  const lockedSet = new Set(locked ?? []);
  return (
    <div>
      <div
        style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 600,
          color: 'var(--text-muted)',
          marginBottom: 4,
        }}
      >
        {title}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {options.map((opt) => {
          const isLocked = lockedSet.has(opt);
          const isOn = isLocked || selectedSet.has(opt);
          return (
            <button
              key={String(opt)}
              type="button"
              className={
                isOn ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'
              }
              disabled={isLocked}
              title={isLocked ? 'Always included' : undefined}
              style={isLocked ? { opacity: 0.7, cursor: 'default' } : undefined}
              onClick={() => {
                if (!isLocked) onToggle(opt);
              }}
            >
              {labelOf(opt)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const FilterSectionsContent = ({
  value,
  onChange,
  locked,
  active,
  clearAll,
}: {
  value: UserFilterState;
  onChange: (next: UserFilterState) => void;
  locked?: UserFilterState;
  active: number;
  clearAll: () => void;
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <span
        style={{
          fontSize: 'var(--fs-sm)',
          fontWeight: 600,
          color: 'var(--text)',
        }}
      >
        Filters
      </span>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={clearAll}
        disabled={active === 0}
      >
        Clear all
      </button>
    </div>
    <hr
      style={{
        border: 'none',
        borderTop: '1px solid var(--border)',
        margin: 0,
      }}
    />

    <FilterSection
      title="Gender"
      options={GENDER_VALUES}
      selected={value.gender}
      labelOf={(v) => ProfileGenderLabel[v as ProfileGender]}
      onToggle={(v) =>
        onChange({ ...value, gender: toggleNumber(value.gender, v) })
      }
    />

    <FilterSection
      title="Onboarding step"
      options={ONBOARDING_STEP_VALUES}
      selected={value.onboardingStep}
      labelOf={(v) => ONBOARDING_STEP_LABEL[v as OnboardingStep]}
      onToggle={(v) =>
        onChange({
          ...value,
          onboardingStep: toggleNumber(value.onboardingStep, v),
        })
      }
    />

    <FilterSection<INTERVIEW_TOPIC | 'none'>
      title="Interview topic"
      options={INTERVIEW_TOPIC_VALUES}
      selected={value.interviewTopic}
      labelOf={(v) => INTERVIEW_TOPIC_DISPLAY[v]}
      onToggle={(v) =>
        onChange({
          ...value,
          interviewTopic: toggleTopic(value.interviewTopic, v),
        })
      }
    />

    <FilterSection
      title="Account status"
      options={ACCOUNT_STATUS_VALUES}
      selected={value.accountStatus}
      locked={locked?.accountStatus}
      labelOf={(v) => AccountStatusLabel[v as AccountStatus]}
      onToggle={(v) =>
        onChange({
          ...value,
          accountStatus: toggleNumber(value.accountStatus, v),
        })
      }
    />

    <FilterSection
      title="Membership"
      options={MEMBERSHIP_TYPE_VALUES}
      selected={value.membershipType}
      labelOf={(v) => MembershipTypeLabel[v as MembershipType]}
      onToggle={(v) =>
        onChange({
          ...value,
          membershipType: toggleNumber(value.membershipType, v),
        })
      }
    />

    <FilterSection
      title="User type"
      options={USER_TYPE_VALUES}
      selected={value.userType}
      locked={locked?.userType}
      labelOf={(v) => UserTypeLabel[v as UserType]}
      onToggle={(v) =>
        onChange({
          ...value,
          userType: toggleNumber(value.userType, v),
        })
      }
    />

    <div>
      <div
        style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 600,
          color: 'var(--text-muted)',
          marginBottom: 4,
        }}
      >
        Age range
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Input
          size="sm"
          type="number"
          min={0}
          max={120}
          style={{ width: 80 }}
          placeholder="Min"
          value={value.ageMin ?? ''}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            onChange({ ...value, ageMin: isNaN(n) ? undefined : n });
          }}
        />
        <span
          style={{
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-muted)',
          }}
        >
          to
        </span>
        <Input
          size="sm"
          type="number"
          min={0}
          max={120}
          style={{ width: 80 }}
          placeholder="Max"
          value={value.ageMax ?? ''}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            onChange({ ...value, ageMax: isNaN(n) ? undefined : n });
          }}
        />
      </div>
    </div>
  </div>
);

const UserFilters = ({ value, onChange, locked, inline }: UserFiltersProps) => {
  const active = countActive(value, locked);
  // Clearing keeps the locked baseline (e.g. win-back's ACTIVE / NORMAL).
  const clearAll = () => onChange({ ...(locked ?? {}) });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  if (inline) {
    return (
      <FilterSectionsContent
        value={value}
        onChange={onChange}
        locked={locked}
        active={active}
        clearAll={clearAll}
      />
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <Button
        variant="secondary"
        rightIcon="chevron-down"
        onClick={() => setOpen((o) => !o)}
      >
        Filters
        {active > 0 && (
          <span
            className="badge badge-purple badge-sm"
            style={{ marginLeft: 6 }}
          >
            {active}
          </span>
        )}
      </Button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            width: 320,
            zIndex: 70,
            background: 'var(--bg-canvas)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-pop)',
            padding: 16,
          }}
        >
          <FilterSectionsContent
            value={value}
            onChange={onChange}
            locked={locked}
            active={active}
            clearAll={clearAll}
          />
        </div>
      )}
    </div>
  );
};

export default UserFilters;
